import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Missions from './pages/Missions';
import MissionDetail from './pages/MissionDetail';
import Matrix from './pages/Matrix';
import Users from './pages/Users';
import Equips from './pages/Equips';
import Theory from './pages/Theory';
import Submissions from './pages/Submissions';
import SubmissionReview from './pages/SubmissionReview';
import Profile from './pages/Profile';
import './styles/App.css';

/**
 * En canviar de pàgina, amunt de tot.
 *
 * React Router no toca el desplaçament: qui arriba a una pantalla nova des del
 * final d'una de llarga hi arriba pel mig. Es nota sobretot anant al llistat de
 * missions des del final d'un exercici, i des que l'entrada amb codi porta
 * directament a `/matrix`, també allà.
 */
function AmuntEnCanviarDePagina() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  // Show footer on all pages except theory
  const showFooter = location.pathname !== '/theory';
  // Dues pantalles no caben dins dels 1.200 px de la resta de l'aplicació: la
  // de l'examen —la matriu en demana 1.260 amb sis hipòtesis— i la d'usuaris,
  // que té sis columnes i una d'accions. Aquí, i només aquí, el contingut
  // ocupa l'ample de la finestra.
  const esExamen = location.pathname.startsWith('/matrix/')
    || location.pathname === '/users'
    || location.pathname === '/equips'
    || location.pathname.endsWith('/submissions');

  return (
    <div className="App">
      <AmuntEnCanviarDePagina />
      <Navbar />
      <main className={`main-content${esExamen ? ' main-content--ample' : ''}`}>
        {/* Si una pantalla peta, es veu un missatge i no el blanc. La clau fa
            que en canviar de pàgina la xarxa es reiniciï sola. */}
        <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* La teoria demana sessió des del 06/08/2026: és material del curs,
              no una pàgina pública. */}
          <Route path="/theory" element={<PrivateRoute><Theory /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/missions" element={<PrivateRoute><Missions /></PrivateRoute>} />
          <Route path="/missions/:id" element={<PrivateRoute><MissionDetail /></PrivateRoute>} />
          <Route path="/missions/:missionId/submissions" element={<PrivateRoute requireInstructor><Submissions /></PrivateRoute>} />
          <Route path="/matrix/:missionId" element={<PrivateRoute><Matrix /></PrivateRoute>} />
          <Route path="/review/:matrixId" element={<PrivateRoute requireInstructor><SubmissionReview /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute requireInstructor><Users /></PrivateRoute>} />
          <Route path="/equips" element={<PrivateRoute requireInstructor><Equips /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </ErrorBoundary>
      </main>
      {showFooter && <Footer />}
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;